import {
  createMemoryHistory,
  createRootRoute,
  createRoute,
  createRouter,
} from '@tanstack/react-router';
import { type ReactElement } from 'react';

import { Layout } from './layout.js';
import { AccountCredentialsPage } from './pages/accounts/account-credentials-page.js';
import { AccountsPage } from './pages/accounts/accounts-page.js';
import { CategoriesPage } from './pages/categories-page.js';
import { DashboardPage } from './pages/dashboard-page.js';
import { EditItemPage } from './pages/edit-item-page.js';
import { MenusPage } from './pages/menus-page.js';
import { ProductPage } from './pages/product-page.js';
import { RestaurantPage } from './pages/restaurant-page.js';
import { ReviewItemPage } from './pages/review-item-page.js';
import { ReviewQueuePage } from './pages/review-queue-page.js';
import { RestaurantMenuPage } from './pages/restaurant-menu-page.js';
import { EnrolPage } from './pages/enrol.js';
import { LoginPage } from './pages/login.js';
import { SettingsPage } from './pages/settings-page.js';
import { UsersPage } from './pages/users-page.js';

/**
 * The Admin_Panel's Route_Tree (admin-panel R3.1, R3.2).
 *
 * The whole URL design, built by hand with `createRoute`/`addChildren`, no file-based
 * generator, a `declare module` making a wrong path a compile error. The panel declares
 * every route; no capability contributes one.
 *
 * The fast-food Product's review-and-menus work is first-class now rather than one
 * Bespoke_Page. The two destinations under the Product are the Review_Queue and the
 * Menus list; a Restaurant is a hub beneath Menus with its own Menu (reviewed items)
 * and its own Categories, because Categories are a Restaurant's — one managed set per
 * Restaurant, not a single panel-wide page. Reviewing a queued item and editing a
 * reviewed item are the same full-page editor flow on two routes: the review route
 * resolves the item out of the queue and advances through it; the edit route resolves
 * one reviewed item of one Restaurant. The panel's own administration — the Users and
 * Settings pages — are the `/admin/*` routes, a section distinct from any Product. A
 * Product's remaining pages — every Declared_Page and the allergen-booklet Bespoke_Page
 * — are still reached under `/products/$productKey/$pageKey`, and the page framework
 * dispatches on the key.
 *
 * ── The Restaurant sub-tree ──────────────────────────────────────────────────
 *
 * ```text
 * /products/$productKey/menus                                  the Restaurants
 * /products/$productKey/menus/$restaurantKey                   one Restaurant (the hub)
 * /products/$productKey/menus/$restaurantKey/items             its reviewed menu
 * /products/$productKey/menus/$restaurantKey/items/$itemKey    edit one reviewed item
 * /products/$productKey/menus/$restaurantKey/categories        its managed Categories
 * ```
 *
 * ── Route ordering ──────────────────────────────────────────────────────────
 *
 * The static Product sub-routes carry literal first segments — `review` and `menus` —
 * while the catch-all carries the `$pageKey` param. TanStack ranks a literal segment
 * above a param at the same depth, so `/products/x/menus` matches {@link menusRoute} and
 * never the `$pageKey` route, and `/products/x/support` falls through to it. Beneath a
 * Restaurant the literals `items` and `categories` likewise outrank nothing — there is
 * no param sibling at that depth — so the sub-tree resolves unambiguously. The order
 * they are listed in `addChildren` does not decide this — the router ranks by
 * specificity — but they are listed static-first so the tree reads the way it resolves.
 */
const rootRoute = createRootRoute({ component: Layout });

/** `/` — the panel home, the Products and their live counts. */
const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: DashboardPage,
});

/** `/products/$productKey/review` — the Review_Queue as a list. */
const reviewQueueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/products/$productKey/review',
  component: ReviewQueuePage,
});

/** `/products/$productKey/review/$itemKey` — the focused review of one queued item. */
const reviewItemRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/products/$productKey/review/$itemKey',
  component: ReviewItemPage,
});

/** `/products/$productKey/menus` — the Restaurants whose menus the panel publishes. */
const menusRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/products/$productKey/menus',
  component: MenusPage,
});

/** `/products/$productKey/menus/$restaurantKey` — one Restaurant, the hub into its work. */
const restaurantRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/products/$productKey/menus/$restaurantKey',
  component: RestaurantPage,
});

/** `/products/$productKey/menus/$restaurantKey/items` — the Restaurant's reviewed menu. */
const restaurantMenuRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/products/$productKey/menus/$restaurantKey/items',
  component: RestaurantMenuPage,
});

/** `.../menus/$restaurantKey/items/$itemKey` — the full-page edit of one reviewed item. */
const editItemRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/products/$productKey/menus/$restaurantKey/items/$itemKey',
  component: EditItemPage,
});

/** `.../menus/$restaurantKey/categories` — one Restaurant's managed Categories. */
const categoriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/products/$productKey/menus/$restaurantKey/categories',
  component: CategoriesPage,
});

/** `/login` — the Sole_Factor_Login, rendered outside the shell by the Layout (R9.3). */
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

/**
 * `/enrol` — the first-key enrolment screen, rendered outside the shell (ADR 0050).
 *
 * Anonymous-reachable: the bootstrap administrator holds a one-time ticket and no session,
 * so it precedes the first sign-in. The bootstrap script prints an `/enrol?ticket=...`
 * link, so the ticket arrives in the address; `validateSearch` reads it off the query,
 * coercing anything but a string to the empty string so a missing or malformed ticket is
 * the empty-field case rather than a routing fault. The empty string is an ordinary value
 * the form renders a blank ticket field for.
 */
const enrolRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/enrol',
  component: EnrolPageComponent,
  validateSearch: (search: Record<string, unknown>): { ticket: string } => ({
    ticket: typeof search.ticket === 'string' ? search.ticket : '',
  }),
});

/** `/admin/users` — the panel's own operator administration (panel-admin). */
const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/users',
  component: UsersPage,
});

/** `/admin/accounts` — create accounts and manage who may sign in (R9.4). */
const accountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/accounts',
  component: AccountsPage,
});

/** `/admin/accounts/$userId/credentials` — one account's security keys (R9.4). */
const accountCredentialsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/accounts/$userId/credentials',
  component: AccountCredentialsPageComponent,
});

/** `/admin/settings` — the panel's own appearance and preferences (panel-admin). */
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/settings',
  component: SettingsPage,
});

/** `/products/$productKey/$pageKey` — a Product's Declared_Page or Bespoke_Page. */
const productPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/products/$productKey/$pageKey',
  component: ProductPageComponent,
});

const routeTree = rootRoute.addChildren([
  dashboardRoute,
  loginRoute,
  enrolRoute,
  reviewQueueRoute,
  reviewItemRoute,
  menusRoute,
  restaurantRoute,
  restaurantMenuRoute,
  editItemRoute,
  categoriesRoute,
  usersRoute,
  accountsRoute,
  accountCredentialsRoute,
  settingsRoute,
  productPageRoute,
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function ProductPageComponent(): ReactElement {
  const { productKey, pageKey } = productPageRoute.useParams();
  return <ProductPage productKey={productKey} pageKey={pageKey} />;
}

function EnrolPageComponent(): ReactElement {
  const { ticket } = enrolRoute.useSearch();
  return <EnrolPage ticket={ticket} />;
}

function AccountCredentialsPageComponent(): ReactElement {
  const { userId } = accountCredentialsRoute.useParams();
  return <AccountCredentialsPage userId={userId} />;
}

/**
 * A router positioned at `path`, over the same tree, for a Fixture to render a page
 * without a browser history.
 */
export function createRouterAt(
  path: string,
): ReturnType<typeof createRouter<typeof routeTree, 'never', true>> {
  return createRouter({
    routeTree,
    history: createMemoryHistory({ initialEntries: [path] }),
  });
}
